import React, { useState, useCallback, useMemo } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router';
import { useDispatch } from 'react-redux';
import { toast } from 'react-toastify';
import MagicButton from 'components/MagicButton/MagicButton';
import { Points } from 'components/Points/Points';
import { BalanceWarning } from 'components/BalanceWarning';
import { AssetUrls } from 'config/assets';
import {
  useCreateTradeLockMutation,
  useReleaseTradeLockMutation,
  useInitiateTradeMutation,
} from 'store/services/trade/tradeApi';
import { useGetPublicProfileQuery } from 'store/services/profile/profileApi';
import { publicCollectionApi } from 'store/services/collection/publicCollectionApi';
import { useProfileTradeSidebar } from '../../context/ProfileTradeSidebarContext';
import TradeSidebarCard from './TradeSidebarCard';
import './TradeSidebar.css';

export const TradeSidebar: React.FC = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const {
    cards,
    isOpen,
    receiverUsername,
    receiverId,
    totalCards,
    totalPoints,
    removeCard,
    updateCard,
    clearAll,
    closeSidebar,
  } = useProfileTradeSidebar();

  // Fetch receiver's profile to get their available points
  const { data: receiverProfile } = useGetPublicProfileQuery(receiverUsername || '', {
    skip: !receiverUsername,
  });

  // Calculate if the trade exceeds receiver's available points
  const { isOverBudget, overageAmount } = useMemo(() => {
    if (!receiverProfile || totalPoints === 0) {
      return { isOverBudget: false, overageAmount: 0 };
    }
    const over = totalPoints > receiverProfile.points;
    return {
      isOverBudget: over,
      overageAmount: over ? totalPoints - receiverProfile.points : 0,
    };
  }, [receiverProfile, totalPoints]);

  const [createTradeLock] = useCreateTradeLockMutation();
  const [releaseTradeLock] = useReleaseTradeLockMutation();
  const [initiateTrade] = useInitiateTradeMutation();
  const [isCreatingTrade, setIsCreatingTrade] = useState(false);
  const [calculatingIds, setCalculatingIds] = useState<Set<string>>(new Set());

  const handleCalculatingChange = useCallback((id: string, isCalc: boolean) => {
    setCalculatingIds(prev => {
      const next = new Set(prev);
      isCalc ? next.add(id) : next.delete(id);
      return next;
    });
  }, []);

  const anyCalculating = calculatingIds.size > 0;

  const handleCreateTrade = async () => {
    if (!receiverId || cards.length === 0) return;

    setIsCreatingTrade(true);
    let acquiredLockToken: string | null = null;

    try {
      // Step 1: Acquire trade lock
      const lockResult = await createTradeLock({ receiverId }).unwrap();

      if (!lockResult.success || !lockResult.lockToken) {
        toast.error(lockResult.message || 'Failed to acquire trade lock. Please try again.');
        setIsCreatingTrade(false);
        return;
      }

      acquiredLockToken = lockResult.lockToken;

      // Step 2: Initiate the trade
      const tradeResult = await initiateTrade({
        receiverId,
        lockToken: acquiredLockToken,
        source: 'profile',
        cards: cards.map(card => ({
          wantListId: card.wantListId,
          cardId: card.cardId?.toString(),
          quantity: 1, // Each sidebar card is a single instance
          condition: card.condition,
          language: card.language,
          foil: card.foil,
          set: card.set,
        })),
      }).unwrap();

      if (tradeResult.success) {
        toast.success('Trade created successfully!');

        // Invalidate the want list cache for this user so traded cards are removed
        if (receiverUsername) {
          dispatch(publicCollectionApi.util.invalidateTags([
            { type: 'PublicWantList', id: receiverUsername },
          ]));
        }

        clearAll();
        navigate('/sending');
      } else {
        toast.error(tradeResult.message || 'Failed to create trade. Please try again.');
        // Release lock on failure
        if (acquiredLockToken) {
          await releaseTradeLock({ receiverId, lockToken: acquiredLockToken });
        }
      }
    } catch (error: any) {
      console.error('Trade creation error:', error);
      // Blocked pair: both the lock and initiate endpoints reject with a
      // generic coded 403 — show a generic message, never a block detail
      if (error?.status === 403 && error?.data?.code === 'TRADE_NOT_ALLOWED') {
        toast.error("You can't start this trade with this user.");
      } else {
        const errorMessage = error?.data?.message || error?.message || 'Failed to create trade. Please try again.';
        toast.error(errorMessage);
      }

      // Release lock on error
      if (acquiredLockToken && receiverId) {
        try {
          await releaseTradeLock({ receiverId, lockToken: acquiredLockToken });
        } catch (releaseError) {
          console.error('Failed to release lock:', releaseError);
        }
      }
    } finally {
      setIsCreatingTrade(false);
    }
  };

  const handleClearAll = () => {
    if (window.confirm('Are you sure you want to remove all cards from your trade?')) {
      clearAll();
    }
  };

  const sidebarContent = (
    <div className={`trade-sidebar ${isOpen ? 'open' : ''}`}>
        {/* Header */}
        <div className="trade-sidebar-header">
          <div className="trade-sidebar-header-left">
            <div className="trade-sidebar-title-row">
              <span className="trade-sidebar-title">
                Sending to {receiverUsername || 'User'}
              </span>
              <Points type="small" points={receiverProfile?.points ?? 0} />
            </div>
            <span className="trade-sidebar-count">
              {totalCards} {totalCards === 1 ? 'Card' : 'Cards'}
            </span>
          </div>
          <button className="trade-sidebar-close" onClick={closeSidebar}>
            <img
              src={AssetUrls.icons.dropdowns.close}
              alt="Close"
              className="trade-sidebar-close-icon"
            />
          </button>
        </div>

        {/* Card list */}
        <div className="trade-sidebar-cards">
          {cards.length === 0 ? (
            <div className="trade-sidebar-empty">
              <img
                src={AssetUrls.icons.navigation.sending}
                alt=""
                className="trade-sidebar-empty-icon"
              />
              <p className="trade-sidebar-empty-text">
                Hover over cards in the want list and click "Add to Trade" to build your trade package.
              </p>
            </div>
          ) : (
            cards.map(card => (
              <TradeSidebarCard
                key={card.id}
                card={card}
                onUpdate={updateCard}
                onRemove={removeCard}
                onCalculatingChange={handleCalculatingChange}
              />
            ))
          )}
        </div>

        {/* Footer */}
        <div className="trade-sidebar-footer">
          <div className="trade-sidebar-total">
            <span className="trade-sidebar-total-label">You will earn:</span>
            <Points points={totalPoints} type="big" isOver={isOverBudget} />
          </div>

          {isOverBudget && receiverUsername && (
            <BalanceWarning username={receiverUsername} pointsShort={overageAmount} />
          )}

          <div className="trade-sidebar-actions">
            {cards.length > 0 && (
              <MagicButton
                label="Clear All"
                color="dark-grey"
                onPress={handleClearAll}
                isDisabled={isCreatingTrade}
              />
            )}
            <MagicButton
              label={isCreatingTrade ? 'Creating...' : 'Create Trade'}
              color="purple"
              onPress={handleCreateTrade}
              isDisabled={cards.length === 0 || isCreatingTrade || anyCalculating}
              isLoading={isCreatingTrade}
            />
          </div>
        </div>
      </div>
  );

  return createPortal(sidebarContent, document.body);
};

export default TradeSidebar;
